import type { ProjectBrief, ScheduleSummary, Task } from '@dyspatch/core';
import {
  applySort,
  byNumber,
  byRank,
  byText,
  byTime,
  type SortOption,
  type SortState,
} from './sorting';
import type { TaskPageGroup } from './task-groups';

/**
 * The key lists, group-sorting and truncation for the three work pages the
 * sort primitive does not draw a table for on its own (issue #74): `/tasks`,
 * `/project/:name`'s two lists, and `/schedules`.
 *
 * This lives in `lib/` rather than beside each route, for the reason
 * `admin-sort.ts` gives for its three pages: a route file pulls in
 * `@/components/*` through the `@/` alias, so a route module cannot be
 * imported from `packages/hub/test` at all. This file only imports types
 * (erased at compile time) and `./sorting`/`./task-groups`, so
 * `packages/hub/test/sort-work-pages.test.ts` can reach it the same way
 * `admin-sort.test.ts` reaches `admin-sort.ts` -- by relative path, never the
 * alias, and with no DOM required to import it.
 */

/**
 * What a step list can be ordered by (issue #74).
 *
 * `waiting` is first, so it is the default: the hub already sorts the "Waiting
 * on a human" group this way inline (`task-groups.ts`), and that is the
 * question a step list answers first -- which one has been sitting longest.
 * Its natural end is ascending, not descending like every other time here,
 * because the interesting end of a wait is the oldest one, not the newest.
 */
export function taskSortOptions(): SortOption<Task>[] {
  return [
    {
      value: 'waiting',
      label: 'Waiting since',
      compare: byTime((task) => task.reviewEnteredAt ?? task.updatedAt),
      natural: 'asc',
    },
    // Groups steps by the job they belong to, so the ones a person is looking
    // for line up. There is no brief title on a step row to sort by instead;
    // the id is stable and this page does not fetch briefs.
    { value: 'job', label: 'Job', compare: byText((task) => task.briefId) },
    {
      value: 'size',
      label: 'Size',
      compare: byRank(['xs', 's', 'm', 'l'], (task) => task.size),
      natural: 'desc',
    },
    { value: 'repo', label: 'Repo', compare: byText((task) => task.repo) },
    {
      value: 'attempts',
      label: 'Attempts',
      compare: byNumber((task) => task.attempts),
      natural: 'desc',
    },
    {
      value: 'updated',
      label: 'Last activity',
      compare: byTime((task) => task.updatedAt),
      natural: 'desc',
    },
  ];
}

/**
 * The order is chosen by the reader and applied inside each group, never
 * across them: the grouping is the page's own answer to "what is this", and a
 * sort that moved a step from Failed into Running would delete it.
 */
export function sortedTaskGroups(
  groups: readonly TaskPageGroup[],
  state: SortState,
): TaskPageGroup[] {
  return groups.map((group) => ({
    ...group,
    tasks: applySort(group.tasks, taskSortOptions(), state, (task) => task.id),
  }));
}

/**
 * What a schedule list can be ordered by (issue #74).
 *
 * `nextRun` is first, so it is the default: it is the question this page adds
 * over `/jobs`, and its natural end is ascending, not descending like a past
 * time -- the interesting one is the soonest, not the furthest off.
 */
export function scheduleSortOptions(): SortOption<ScheduleSummary>[] {
  return [
    {
      value: 'nextRun',
      label: 'Next run',
      compare: byTime((schedule) => schedule.nextRunAt),
      natural: 'asc',
    },
    { value: 'name', label: 'Name', compare: byText((schedule) => schedule.name) },
    {
      value: 'lastRun',
      label: 'Last run',
      compare: byTime((schedule) => schedule.lastRunAt),
      natural: 'desc',
    },
  ];
}

/** One of the groups `SchedulesPage` sorts, kept next to the function that sorts them. */
export interface ScheduleGroup {
  key: string;
  label: string;
  rows: ScheduleSummary[];
  empty: string;
}

/**
 * Chosen by the reader, applied inside each group and never across them: the
 * three groups here are whether a schedule is paused and on whom, which is a
 * fact from #21/#46, not a preference a sort should be able to erase.
 */
export function sortedScheduleGroups(
  groups: readonly ScheduleGroup[],
  state: SortState,
): ScheduleGroup[] {
  return groups.map((group) => ({
    ...group,
    rows: applySort(group.rows, scheduleSortOptions(), state, (schedule) => schedule.id),
  }));
}

/**
 * What is neither alphabetical nor numeric, in the order a person on this repo
 * would rather see first: waiting on somebody, then running, then queued,
 * then the two states nobody has to act on again.
 */
const TASK_STATE_RANK = [
  'review',
  'running',
  'reporting',
  'queued',
  'offered',
  'claimed',
  'draft',
  'failed',
  'done',
  'cancelled',
];

/**
 * What this repo's step list can be ordered by (issue #74). `updated` is
 * first, so a reader who never touches the control sees this repo's own order
 * (newest touched first), which is what the list showed before this existed.
 */
export function stepSortOptions(): SortOption<Task>[] {
  return [
    {
      value: 'updated',
      label: 'Last activity',
      compare: byTime((task) => task.updatedAt),
      natural: 'desc',
    },
    { value: 'state', label: 'State', compare: byRank(TASK_STATE_RANK, (task) => task.status) },
    { value: 'key', label: 'Key', compare: byText((task) => task.key) },
    {
      value: 'size',
      label: 'Size',
      compare: byRank(['xs', 's', 'm', 'l'], (task) => task.size),
      natural: 'desc',
    },
  ];
}

/**
 * What repo names, so it lives next to the same rank a step's state uses.
 * `awaiting_approval` first: that is the one state on this list a person
 * still has to act on.
 */
const BRIEF_STATE_RANK = [
  'awaiting_approval',
  'decomposing',
  'approved',
  'draft',
  'failed',
  'rejected',
  'done',
];

/** What "Jobs naming this repo" can be ordered by (issue #74). */
export function jobSortOptions(): SortOption<ProjectBrief>[] {
  return [
    {
      value: 'updated',
      label: 'Last activity',
      compare: byTime((brief) => brief.updatedAt),
      natural: 'desc',
    },
    { value: 'title', label: 'Title', compare: byText((brief) => brief.title) },
    { value: 'state', label: 'State', compare: byRank(BRIEF_STATE_RANK, (brief) => brief.status) },
  ];
}

/** The pair of search keys each of `/project/:name`'s two controls owns (issue #74). */
export const STEP_SORT_KEYS = { sort: 'stepSort', dir: 'stepDir' };
export const JOB_SORT_KEYS = { sort: 'jobSort', dir: 'jobDir' };

/** How many rows each of `/project/:name`'s two lists shows. */
export const STEP_LIST_LIMIT = 60;
export const JOB_LIST_LIMIT = 20;

/**
 * Ordered before it is cut down to what the page shows: sorting the truncated
 * slice instead would order an arbitrary `STEP_LIST_LIMIT` rather than the
 * whole list, and a reader who picks "Last activity" expects the most recent
 * of everything, not of whatever happened to survive the cut.
 */
export function orderedSteps(tasks: readonly Task[], state: SortState): Task[] {
  return applySort(tasks, stepSortOptions(), state, (task) => task.id).slice(0, STEP_LIST_LIMIT);
}

/** The same guarantee as `orderedSteps`, for "Jobs naming this repo". */
export function orderedJobs(briefs: readonly ProjectBrief[], state: SortState): ProjectBrief[] {
  return applySort(briefs, jobSortOptions(), state, (brief) => brief.id).slice(0, JOB_LIST_LIMIT);
}
